import { Grid, Paper } from "@material-ui/core";
import { LaneRequestParameters } from "api/lokiRequestModels/LaneRequestParameters";
import { services } from "api/serviceConfig";
import ExpandableStatisticsComponent from "components/ExpandableStatComponent/ExpandableStatisticsComponent";
import { TempProfileChart } from "components/TempProfileChart/TempProfileChart";
import { dateEndFormatting, dateStartFormatting } from "helpers/dateFormatting";
import { LaneStatistics } from "models/LaneStatistics";
import { LaneTempProfile } from "models/laneTempProfile/LaneTempProfile";
import { LaneTempStatistics } from "models/LaneTempStatistics";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useStore } from "store/StoreConfigs";
import { expandableAreaStyles } from "./ExpandableAreaStyles";

export default function ExpandableArea(props: any) {
    const classes = expandableAreaStyles();
    const { filtersStore, identityStore } = useStore();
    const laneId = props.laneId;

    const [loadingTempStatistics, setLoadingTempStatistics] = useState(false);
    const [loadingTripStatistics, setLoadingTripStatistics] = useState(false);
    const [loadingTempProfile, setLoadingTempProfile] = useState(false);

    const [tempItems, setTempItems] = useState<LaneTempProfile[]>([]);
    const [laneTripStat, setTripLaneStat] = useState<LaneStatistics>();
    const [laneTempStat, setTempLaneStat] = useState<LaneTempStatistics>();

    useEffect(() => {
        const request: LaneRequestParameters = {
            startTime: dateStartFormatting(filtersStore.startDate),
            endTime: dateEndFormatting(filtersStore.endDate),
            customerId: identityStore.currentCustomerId,
            laneId,
            lsp: filtersStore.selectedLogisticsServiceProviders.join(",") || undefined,
            productType: filtersStore.selectedProductTypes.join(",") || undefined,
            riskLevel: filtersStore.selectRiskLevels.join(",") || undefined,
            transportationModes: filtersStore.selectedModesOfTransportation.join(",") || undefined,
            packagingType: filtersStore.selectedPackagingTypes.join(",") || undefined,
        };

        const loadTripStatistics = async () => {
            setLoadingTripStatistics(true);

            const response = await services.Lane.getLaneTripStatistics(request);

            if (response !== undefined) {
                setTripLaneStat(response.data);
            }

            setLoadingTripStatistics(false);
        };

        const loadTempStatistics = async () => {
            setLoadingTempStatistics(true);

            const response = await services.Lane.getLaneTemperatureStatistics(request);

            if (response !== undefined) {
                setTempLaneStat(response.data);
            }

            setLoadingTempStatistics(false);
        };

        const loadTempProfile = async () => {
            setLoadingTempProfile(true);

            const response = await services.Lane.getLaneTemperatureProfile(request);

            if (response !== undefined) {
                setTempItems(response.data.laneTempProfile);
            }

            setLoadingTempProfile(false);
        };

        loadTripStatistics();
        loadTempStatistics();
        loadTempProfile();
    }, []);

    return (
        <Paper className={classes.expandArea} elevation={0}>
            <Grid container direction="row" alignContent="space-between">
                <Grid item className={classes.gridComponent}>
                    <ExpandableStatisticsComponent
                        isLoading={loadingTripStatistics}
                        title={"Trip Duration"}
                        min={
                            laneTripStat?.minTripDuration === null
                                ? "No data"
                                : `${(laneTripStat?.minTripDuration || 0).toFixed(2)} hours`
                        }
                        max={
                            laneTripStat?.maxTripDuration === null
                                ? "No data"
                                : `${(laneTripStat?.maxTripDuration || 0).toFixed(2)} hours`
                        }
                        mean={
                            laneTripStat?.meanTripDuration === null
                                ? "No data"
                                : `${(laneTripStat?.meanTripDuration || 0).toFixed(2)} hours`
                        }
                        stdDev={
                            laneTripStat?.tripDurationStandardDeviation === null
                                ? "No data"
                                : `${(laneTripStat?.tripDurationStandardDeviation || 0).toFixed(2)} hours`
                        }
                    />
                </Grid>
                <Grid item className={classes.gridComponent}>
                    <ExpandableStatisticsComponent
                        isLoading={loadingTempStatistics}
                        title={"Temperature"}
                        min={`${(((laneTempStat?.minTemperature || 0) - 32) / 1.8)?.toFixed(2)}°C`}
                        max={`${(((laneTempStat?.maxTemperature || 0) - 32) / 1.8)?.toFixed(2)}°C`}
                        mean={`${(((laneTempStat?.meanTemperature || 0) - 32) / 1.8)?.toFixed(2)}°C`}
                        stdDev={`${((laneTempStat?.temperatureStandardDeviation || 0) / 1.8)?.toFixed(2)}°C`}
                    />
                </Grid>

                <Grid className={classes.graphWidth} item>
                    <TempProfileChart
                        minTimestamp={moment.utc(filtersStore.startDate).valueOf()}
                        maxTimestamp={moment.utc(filtersStore.endDate).valueOf()}
                        tempItems={tempItems}
                        isLoading={loadingTempProfile}
                    />
                </Grid>
            </Grid>
        </Paper>
    );
}
